import "~/features/quote/components/quote-details/styles/quote-details-items.css";
import "~/shared/components/commodities/commodity-handling-unit.css";

import { Checkbox } from "@tql/ui-components-react/kendo/inputs";
import { Label } from "@tql/ui-components-react/kendo/labels";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { useQuoteDetailsPageLoadQueries } from "~/features/quote/hooks/use-quote-details-hooks.ts";
import { InputFieldsGroup } from "~/shared/components/input-group.tsx";
import type { InputFieldConfig } from "~/shared/types/input-group-types.ts";
import type {
	HandlingUnit,
	OnChange,
} from "~/shared/types/quote-details-types";
import { getEstClass } from "~/shared/utils/commodity-utils.ts";

import { FreightHazmatFields } from "./hazmat-fields.tsx";

type FreightCommodityFieldsProps = {
	handlingUnit: HandlingUnit;
	idx: number;
	updateItem: (
		handlingUnit: HandlingUnit,
		field: keyof HandlingUnit,
		value: OnChange | null,
	) => void;
	measuringType?: "LB/IN" | "KG/CM";
	onImperialMetricConversion?: (unit: "LB/IN" | "KG/CM", id?: number) => void;
	validationErrors?: Set<string>;
	onClearFieldError?: (fieldKey: string) => void;
	showHazmatDetails?: boolean;
	requireDescription?: boolean;
	requireClass?: boolean;
	requirePieceCount?: boolean;
};

// Fixed per-field widths (from the design) keep the whole handling unit on one
// row; otherwise each field grows to its Kendo input's intrinsic width and wraps.
const getFieldWrapperStyle = (
	width: number,
): InputFieldConfig["wrapperStyle"] => ({
	flex: "none",
	width,
});

/**
 * Shipment-management commodity editor. A trimmed sibling of the quote flow's
 * `CommodityHandlingUnit`: it renders the same handling-unit fields (using the
 * shared reference-data queries, input group and hazmat row) but is driven
 * entirely by props — no `QuoteDetailsContext` — and drops the quote-only
 * "save to commodity book" action.
 */
export const FreightCommodityFields = ({
	handlingUnit,
	idx,
	updateItem,
	measuringType = "LB/IN",
	onImperialMetricConversion,
	validationErrors,
	onClearFieldError,
	showHazmatDetails,
	requireDescription,
	requireClass = true,
	requirePieceCount = false,
}: FreightCommodityFieldsProps) => {
	const { data } = useQuoteDetailsPageLoadQueries();
	const freightClassData = data?.freightClassData;
	const unitTypes = data?.unitTypes;
	const hazmatClasses = data?.hazmatClasses;
	const hazmatGroups = data?.hazmatGroups;

	const getDimensionType = measuringType === "LB/IN" ? "in" : "cm";

	const formatDecimalDisplay = useCallback((value?: number | null) => {
		if (value == null || value === 0) {
			return "—";
		}
		return (Math.floor(value * 100) / 100).toFixed(2);
	}, []);

	const prevSelectedUnit = useRef(measuringType);

	useEffect(() => {
		if (prevSelectedUnit.current !== measuringType) {
			if (onImperialMetricConversion) {
				onImperialMetricConversion(measuringType, handlingUnit.id);
			}
			prevSelectedUnit.current = measuringType;
		}
	}, [handlingUnit.id, onImperialMetricConversion, measuringType]);

	const fullFieldSet: InputFieldConfig[] = useMemo(
		() => [
			{
				key: "description",
				label: "Commodity Description",
				type: "input",
				value: handlingUnit.description ?? "",
				labelId: `description-label-${handlingUnit.id}`,
				required: !!requireDescription,
				wrapperStyle: getFieldWrapperStyle(155),
			},
			{
				key: "quantity",
				label: "Qty",
				type: "numeric",
				value: handlingUnit.quantity,
				labelId: `quantity-label-${handlingUnit.id}`,
				required: true,
				wrapperStyle: getFieldWrapperStyle(45),
			},
			{
				key: "unit",
				label: "Unit",
				type: "dropdown",
				value: handlingUnit.unit ?? { id: 0, name: "select" },
				data: unitTypes ?? [],
				labelId: `unit-label-${handlingUnit.id}`,
				required: true,
				wrapperStyle: getFieldWrapperStyle(95),
			},
			{
				key: "weight",
				label: "Total Weight",
				type: "numeric",
				value: handlingUnit.weight,
				labelId: `weight-label-${handlingUnit.id}`,
				required: true,
				wrapperStyle: getFieldWrapperStyle(95),
			},
			{
				key: "length",
				label: `L(${getDimensionType})`,
				type: "numeric",
				value: handlingUnit.length,
				labelId: `length-label-${handlingUnit.id}`,
				required: true,
				wrapperStyle: getFieldWrapperStyle(56),
				after: (
					<div className={"x"}>
						<div>X</div>
					</div>
				),
			},
			{
				key: "width",
				label: `W(${getDimensionType})`,
				type: "numeric",
				value: handlingUnit.width,
				labelId: `width-label-${handlingUnit.id}`,
				required: true,
				wrapperStyle: getFieldWrapperStyle(56),
				after: <div className={"x"}>X</div>,
			},
			{
				key: "height",
				label: `H(${getDimensionType})`,
				type: "numeric",
				value: handlingUnit.height,
				labelId: `height-label-${handlingUnit.id}`,
				required: true,
				wrapperStyle: getFieldWrapperStyle(56),
			},
			{
				key: "class",
				label: "Class",
				type: "dropdown",
				value: handlingUnit.class ?? { id: 0, name: "" },
				data: freightClassData ?? [],
				labelId: `class-label-${handlingUnit.id}`,
				required: requireClass,
				textField: "code",
				clearable: true,
				clearLabel: "",
				clearValue: { id: 0, name: "", code: "" },
				wrapperStyle: getFieldWrapperStyle(60),
			},
			{
				key: "nmfc",
				label: "NMFC",
				type: "input",
				value: handlingUnit.nmfc ?? "",
				labelId: `nmfc-label-${handlingUnit.id}`,
				required: false,
				pattern: "0123456789-",
				wrapperStyle: getFieldWrapperStyle(95),
			},
			{
				key: "pieceCount",
				label: "Pc/Case Ct",
				type: "numeric",
				className: "piece-case-count-input",
				value:
					handlingUnit.pieceCount && handlingUnit.pieceCount > 0
						? handlingUnit.pieceCount
						: null,
				labelId: `pieceCount-label-${handlingUnit.id}`,
				required: requirePieceCount,
				show: true,
				wrapperStyle: getFieldWrapperStyle(85),
			},
			{
				key: "density",
				label: "Density",
				type: "display",
				value: formatDecimalDisplay(handlingUnit.density),
				labelId: `density-label-${handlingUnit.id}`,
				hasToolTip: true,
				tooltip:
					"Density is calculated as weight divided by volume (cubic feet). This helps determine freight classification.",
				required: false,
				wrapperStyle: getFieldWrapperStyle(70),
			},
			{
				key: "estClass",
				label: "Est Class",
				type: "display",
				value: (() => {
					const estClassValue = getEstClass(handlingUnit.density ?? 0);
					return estClassValue === 0 ? "—" : estClassValue;
				})(),
				labelId: `estClass-label-${handlingUnit.id}`,
				hasToolTip: true,
				tooltip:
					"The estimated classification is based on density only. Your actual classification will be based on your handling unit's transportation characteristics, including density, stowability, handling and liability. Charges may be incurred if you select the wrong class. If you have questions about your freight’s actual classification, please contact your TQL Account Representative.",
				required: false,
				wrapperStyle: getFieldWrapperStyle(75),
			},
		],
		[
			freightClassData,
			getDimensionType,
			handlingUnit.class,
			handlingUnit.density,
			handlingUnit.description,
			handlingUnit.height,
			handlingUnit.id,
			handlingUnit.length,
			handlingUnit.nmfc,
			handlingUnit.pieceCount,
			handlingUnit.quantity,
			handlingUnit.unit,
			handlingUnit.weight,
			handlingUnit.width,
			formatDecimalDisplay,
			requireDescription,
			requireClass,
			requirePieceCount,
			unitTypes,
		],
	);

	const onChange = useCallback(
		(field: keyof HandlingUnit, value: OnChange | null) => {
			if (onClearFieldError) onClearFieldError(field);
			updateItem(handlingUnit, field, value);
		},
		[handlingUnit, updateItem, onClearFieldError],
	);

	return (
		<>
			<div
				className={"handling-unit-label"}
				id={`commodity-handling-unit-${handlingUnit.id}`}
			>
				Handling Unit {idx + 1}
			</div>
			<div className={"commodity-row"}>
				<div className={"commodity-fields-group"}>
					<InputFieldsGroup
						fields={fullFieldSet}
						groupId={handlingUnit.id}
						onChange={onChange}
						validationErrors={validationErrors}
					/>
				</div>
				<div className={"hazmat-stackable-vertical"}>
					<div className={"stackable-wrapper"}>
						<Checkbox
							aria-describedby={"stackable"}
							checked={!!handlingUnit.isStackable}
							disabled={false}
							id={`stackable-checkbox-${handlingUnit.id}`}
							name={"stackable"}
							onChange={(e) => {
								onChange("isStackable", e.value);
							}}
						/>
						<Label editorId={`stackable-checkbox-${handlingUnit.id}`}>
							Stackable
						</Label>
					</div>
					<div className={"hazmat-wrapper"}>
						<Checkbox
							aria-describedby={"hazmat"}
							checked={!!handlingUnit.isHazmat}
							id={`hazmat-checkbox-${handlingUnit.id}`}
							onChange={(e) => onChange("isHazmat", e.value)}
						/>
						<Label editorId={`hazmat-checkbox-${handlingUnit.id}`}>
							Hazmat
						</Label>
					</div>
				</div>
			</div>

			{showHazmatDetails && handlingUnit.isHazmat ? (
				<FreightHazmatFields
					handlingUnit={handlingUnit}
					hazmatClasses={hazmatClasses}
					hazmatGroups={hazmatGroups}
					onChange={onChange}
					validationErrors={validationErrors}
				/>
			) : null}
		</>
	);
};
